import { Collection, Config, Field, Screen, StatusOption } from "./config";

export interface CompanyRolePermissions {
  employeesVisibleTo?: string[];
  employeesEditableBy?: string[];
  screens?: Record<
    string,
    {
      roles?: string[];
      showAdd?: string[];
      reordering?: string[];
      statusOptions?: StatusOption[];
    }
  >;
  collections?: Record<
    string,
    {
      deletableBy?: string[];
      activityLogs?: {
        roles?: string[];
      };
      fields?: Record<
        string,
        {
          list?: string[];
          form?: string[];
          formCreate?: string[];
          formEdit?: string[];
          board?: string[];
          details?: string[];
          showAdd?: string[];
          statusOptions?: StatusOption[];
        }
      >;
    }
  >;
}

export interface CompanyRoleConfig {
  roles: string[];
  defaultRole?: string;
  permissions: CompanyRolePermissions;
}

const cloneConfig = (config: Config): Config =>
  JSON.parse(JSON.stringify(config)) as Config;

export const removeUndefined = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(removeUndefined) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([entryKey, entryValue]) => [
          entryKey,
          removeUndefined(entryValue),
        ]),
    ) as T;
  }

  return value;
};

const copyArray = (value?: string[]) => (value ? [...value] : undefined);

const extractScreenPermissions = (screen: Screen) => ({
  roles: copyArray(screen.roles),
  showAdd: copyArray(screen.showAdd),
  reordering: copyArray(screen.reordering),
  statusOptions: screen.statusOptions
    ? JSON.parse(JSON.stringify(screen.statusOptions))
    : undefined,
});

const extractFieldPermissions = (field: Field) => ({
  list: copyArray(field.list),
  form: copyArray(field.form),
  formCreate: copyArray(field.formCreate),
  formEdit: copyArray(field.formEdit),
  board: copyArray(field.board),
  details: copyArray(field.details),
  showAdd: copyArray(field.showAdd),
  statusOptions: field.statusOptions
    ? JSON.parse(JSON.stringify(field.statusOptions))
    : undefined,
});

const extractCollectionPermissions = (collection: Collection) => {
  const fields = collection.fields.reduce<
    NonNullable<
      NonNullable<CompanyRolePermissions["collections"]>[string]["fields"]
    >
  >((acc, field) => {
    acc[field.name] = extractFieldPermissions(field);
    return acc;
  }, {});

  return {
    deletableBy: copyArray(collection.deletableBy),
    activityLogs: collection.activityLogs
      ? { roles: copyArray(collection.activityLogs.roles) }
      : undefined,
    fields,
  };
};

export const extractRoleConfig = (config: Config): CompanyRoleConfig =>
  removeUndefined({
    roles: copyArray(config.roles) ?? [],
    defaultRole: config.defaultRole,
    permissions: {
      employeesVisibleTo: copyArray(config.employeesVisibleTo),
      employeesEditableBy: copyArray(config.employeesEditableBy),
      screens: config.screens.reduce<
        NonNullable<CompanyRolePermissions["screens"]>
      >((acc, screen) => {
        acc[screen.name] = extractScreenPermissions(screen);
        return acc;
      }, {}),
      collections: config.database.reduce<
        NonNullable<CompanyRolePermissions["collections"]>
      >((acc, collection) => {
        acc[collection.name] = extractCollectionPermissions(collection);
        return acc;
      }, {}),
    },
  });

const applyDefinedArray = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value?: string[],
) => {
  if (value) {
    target[key] = [...value] as T[K];
  }
};

const applyStatusOptions = (
  target: { statusOptions?: StatusOption[] },
  statusOptions?: StatusOption[],
) => {
  if (statusOptions) {
    target.statusOptions = JSON.parse(JSON.stringify(statusOptions));
  }
};

export const applyRoleConfig = (
  baseConfig: Config,
  roleConfig?: CompanyRoleConfig | null,
): Config => {
  if (!roleConfig) {
    return baseConfig;
  }

  const nextConfig = cloneConfig(baseConfig);

  nextConfig.roles = [...roleConfig.roles];
  nextConfig.defaultRole = roleConfig.defaultRole;

  applyDefinedArray(
    nextConfig,
    "employeesVisibleTo",
    roleConfig.permissions.employeesVisibleTo,
  );
  applyDefinedArray(
    nextConfig,
    "employeesEditableBy",
    roleConfig.permissions.employeesEditableBy,
  );

  nextConfig.screens = nextConfig.screens.map((screen) => {
    const screenPermissions = roleConfig.permissions.screens?.[screen.name];
    if (!screenPermissions) {
      return screen;
    }

    const nextScreen = { ...screen };
    applyDefinedArray(nextScreen, "roles", screenPermissions.roles);
    applyDefinedArray(nextScreen, "showAdd", screenPermissions.showAdd);
    applyDefinedArray(nextScreen, "reordering", screenPermissions.reordering);
    applyStatusOptions(nextScreen, screenPermissions.statusOptions);
    return nextScreen;
  });

  nextConfig.database = nextConfig.database.map((collection) => {
    const collectionPermissions =
      roleConfig.permissions.collections?.[collection.name];
    if (!collectionPermissions) {
      return collection;
    }

    const nextCollection = { ...collection };
    applyDefinedArray(
      nextCollection,
      "deletableBy",
      collectionPermissions.deletableBy,
    );

    if (collectionPermissions.activityLogs && nextCollection.activityLogs) {
      nextCollection.activityLogs = {
        ...nextCollection.activityLogs,
        roles:
          collectionPermissions.activityLogs.roles ??
          nextCollection.activityLogs.roles,
      };
    }

    nextCollection.fields = nextCollection.fields.map((field) => {
      const fieldPermissions = collectionPermissions.fields?.[field.name];
      if (!fieldPermissions) {
        return field;
      }

      const nextField = { ...field };
      applyDefinedArray(nextField, "list", fieldPermissions.list);
      applyDefinedArray(nextField, "form", fieldPermissions.form);
      applyDefinedArray(nextField, "formCreate", fieldPermissions.formCreate);
      applyDefinedArray(nextField, "formEdit", fieldPermissions.formEdit);
      applyDefinedArray(nextField, "board", fieldPermissions.board);
      applyDefinedArray(nextField, "details", fieldPermissions.details);
      applyDefinedArray(nextField, "showAdd", fieldPermissions.showAdd);
      applyStatusOptions(nextField, fieldPermissions.statusOptions);
      return nextField;
    });

    return nextCollection;
  });

  return nextConfig;
};
